import { CIMSPage, pathify } from '../..'
import { CIMSModuleItem } from '../CIMSModule/CIMSModuleItem'
import { PathnameItem, PathnameItemConstructor } from '../PathnameItem'

export class CIMSPageItem<
  ID extends Lowercase<string> = Lowercase<string>,
  Name extends string = string,
  Pathname extends string = string,
  ModuleID extends Lowercase<string> = Lowercase<string>,
  ModuleName extends string = string,
  ModulePathname extends string = string
> extends PathnameItem<ID, Name, Pathname> {
  /**
   * the {@link CIMSModuleItem} associated with this page
   */
  readonly module: CIMSModuleItem<ModuleID, ModuleName, ModulePathname>
  /**
   * whether the page supports deep links
   */
  readonly linkable: boolean
  /**
   * @see {@link PathnameItem}
   */
  constructor({
    module,
    linkable,
    ...pathnameItem
  }: PathnameItemConstructor<ID, Name, Pathname> &
    Pick<
      CIMSPageItem<ID, Name, Pathname, ModuleID, ModuleName, ModulePathname>,
      'name' | 'module'
    > & { linkable?: true }) {
    super(pathnameItem)
    this.module = module
    this.linkable = linkable ?? false
  }

  /**
   * gets all possible deprecated paths for the page
   */
  get deprecatedProjectPaths() {
    const paths: string[] = []
    const { deprecatedPathnames, pathname } = this.module
    if (!deprecatedPathnames.length && !this.deprecatedPathnames.length)
      return paths

    // get all combinations of deprecated + parent's deprecated
    const parents = [...deprecatedPathnames, pathname]
    const children = [...this.deprecatedPathnames, this.pathname]
    parents.forEach((parent) => {
      children.forEach((child) => {
        // ignore current path
        if (parent === pathname && child === this.pathname) return
        const segments = [CIMSPage.projectId, parent, child]
        // need to add this before the default segments to correctly redirect deep links
        if (this.linkable) paths.push(pathify(...segments, CIMSPage.deepLink))
        paths.push(pathify(...segments))
      })
    })
    return paths
  }

  /**
   * constructs the path to get this page in the ui, with a leading `/` and route match
   *
   * assumes the project match to be `:projectId` and the deep link match to be `:deepLink`
   */
  get recordPath() {
    const segments = [CIMSPage.projectId, this.module.pathname, this.pathname]
    if (this.linkable) segments.push(CIMSPage.deepLink)
    return pathify(...segments)
  }

  /**
   * a path to get to this module in the ui, with a leading `/`
   *
   * assumes the project match value to be `:projectId`
   */
  get projectPath() {
    return pathify(CIMSPage.projectId, this.module.pathname, this.pathname)
  }
}
